import React, { useEffect } from 'react';
import { Form } from 'components/ds';
import PageContainer from 'modules/account/components/PageContainer/PageContainer';
import { useGetTemplate } from 'modules/account/configs/SchemaConfig';
import UserNameField from '../../../components/UserNameField/UserNameField';
import { pageConfigs } from '../../../configs/pageConfigs';
import { useSearchUser } from '../../../hooks/useSearchUser';
import { ValidatorField } from '../../../../../components/ValidatorField';
import { useUsername } from '../../../hooks/useUsername';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { resetStorage } from '../Logout';
import { HelpBox } from '../../../components/HelpBox/HelpBox';
import { useNavigationFlow } from '../../../hooks/useNavigationFlow';
import { useGAEvents } from '../../../../../hooks/useGoogleAnalytics';

interface FormValues {
  username: string;
}

export const LoginUserName: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const {data: pageConfig = {}} = useGetTemplate(pageConfigs.loginUserName.template);
  const {searchUser, loading} = useSearchUser();
  const {username, saveUsername} = useUsername();
  const {navigateBasedOnUserData} = useNavigationFlow();
  const { events } = useGAEvents();
  
  const theme = ApiLocalStorage.get('theme') || {};
  
  useEffect(() => {
    if (username) {
      form.setFieldsValue({username});
    }
  }, [form, username]);
  
  useEffect(() => {
    resetStorage();
    // Evento GA4: Início do formulário de login
    events.formStart('login_username');
  }, [events]);
  
  const validateForm = async (): Promise<FormValues> => {
    try {
      const fields = await form.validateFields();
      const cleanUsername = ValidatorField.removeSpecialCharsFromUsername(fields.username);
      
      return {username: cleanUsername};
    } catch (error) {
      throw new Error('Por favor, preencha todos os campos obrigatórios.');
    }
  };
  
  const validateUser = async (username: string, captcha: string) => {
    try {
      const response = await searchUser(username, captcha);
      ApiLocalStorage.set('@pwa-username', username);
      
      if (response) {
        ApiLocalStorage.set('@pwa-participant-id', response?.participantId || '');
        // Evento GA4: Sucesso na validação do username
        events.formSubmit('login_username', true);
        navigateBasedOnUserData(response);
      }
    } catch (error) {
      console.error('Error validating user:', error);
      // Evento GA4: Erro na validação do username
      events.error('username_validation_error', error instanceof Error ? error.message : 'Erro desconhecido');
    }
  };
  
  const handleNext = async (): Promise<void> => {
    ApiLocalStorage.set('@pwa-username', username);
    
    try {
      const {username: formUsername} = await validateForm();
      
      if (username !== formUsername) {
        saveUsername(formUsername);
      }
      
      await validateUser(formUsername, '');
    } catch (error: any) {
      console.error('Erro no handleNext:', error);
      // Evento GA4: Erro no formulário de username
      events.formSubmit('login_username', false);
      events.error('login_form_error', error?.message || 'Erro no formulário de login');
    }
  };

  const needHelpFml = theme?.login?.['helpBox-fml'];
  const helpBoxConfig = theme?.login?.['helpBox-login-fml'];
  
  return (
    <PageContainer
      title={pageConfig.title}
      navigateBack={pageConfigs?.loginUserName?.navigateBack?.()}
      onClick={handleNext}
      buttonText={pageConfigs.loginUserName.buttonLabel}
      disabled={loading}
      loading={loading}
      extraAboveButton={needHelpFml ? (
        <HelpBox
          customTitle={helpBoxConfig?.title}
          customLinkText={helpBoxConfig?.linkText}
          customIcon={helpBoxConfig?.icon}
        />
      ) : undefined}
      thereIsButton
    >
      <Form form={form} layout="vertical" onFinish={handleNext}>
        <UserNameField form={form} isDisabled={loading}/>
      </Form>
    </PageContainer>
  );
};

export default LoginUserName;
